'use client';

import type { ISnowplowContext, ISnowplowUm } from '@fanduel/snowplow-um-contract';
import type { GameTrackingInfo } from '@/types/games';
import { getGameName } from '@/logic/game-adapter/game-adapter';
import { mapPropsToKeys } from '@/utils/tracking/utils';
import { getPageNameFromPathname } from '@/tracking/context/page-name';

let snowplowInstance: ISnowplowUm | null = null;
let initPromise: Promise<ISnowplowUm> | null = null;

export const EVENT_TYPE = {
    GAME_CLICKED: 'game_clicked',
    TOP_NAV_IMPRESSION: 'top_nav_impression',
    TOP_NAV_SCROLL: 'top_nav_scroll',
    TOP_NAV_SELECTED: 'top_nav_selected',
    ZONE_SCROLLED: 'zone_scrolled',
    ZONE_INITIAL_LOAD: 'zone_initial_load',
    GAME_ZONE_IMPRESSION: 'game_zone_impression',
    GAME_ZONE_SCROLL: 'game_zone_scroll',
    GAME_ZONE_SELECTED: 'game_zone_selected',
    CATEGORY_PAGE_IMPRESSION: 'category_page_impression',
    CATEGORY_PAGE_GAME_SELECTED: 'category_page_game_selected',
    ZONE_INTERACTION: 'zone_interaction',
    CATEGORY_SCREEN_VIEW: 'category_screen_view',
    GAME_FAVORITED: 'game_favorited',
    GAME_FAVORITE_REMOVED: 'game_favorite_removed',
    INFO_ICON_CLICKED: 'info_icon_clicked',
    SEARCH_OPENED: 'search_opened',
    SEARCH_INITIALIZED: 'search_initialized',
    SEARCH_RESULTS_RETURNED: 'search_results_returned',
    SEARCH_RESULTS_SELECTED: 'search_results_selected'
} as const;

export const TOP_NAV_SNOWPLOW_BANNER_PROPS = {
    bannerName: 'top_nav',
    bannerId: 'top_nav',
    bannerType: 'navigation',
    eventCategory: 'navigation'
} as const;

export const SNOWPLOW_CONTEXT_MAPPING = {
    USER_PROPERTIES: 'user_properties',
    GLOBAL_PROPERTIES: 'global_properties',
    GAME_IMPRESSION: 'game_impression',
    NAVIGATION_MENU_ELEMENT: 'navigation_menu_element',
    ZONE_INTERACTION: 'zone_interaction'
};

const BANNER_INTERACTION_SCHEMA_4_0_0 = 'iglu:com.fanduel/banner_interaction/jsonschema/4-0-0';
const BANNER_INTERACTION_SCHEMA_4_0_2 = 'iglu:com.fanduel/banner_interaction/jsonschema/4-0-2';
const ZONE_INTERACTION_SCHEMA_1_0_1 = 'iglu:com.fanduel/zone_interaction/jsonschema/1-0-1';

const GAME_ELEMENT_SCHEMA_4_0_1 = 'iglu:com.fanduel/game_element/jsonschema/4-0-1';

const SEARCH_OPENED_SCHEMA_1_0_1 = 'iglu:com.fanduel/search_opened/jsonschema/1-0-1';
const SEARCH_INITIALIZED_SCHEMA_1_0_1 = 'iglu:com.fanduel/search_initialized/jsonschema/1-0-1';
const SEARCH_RESULTS_RETURNED_SCHEMA_1_0_2 = 'iglu:com.fanduel/search_results_returned/jsonschema/1-0-2';
const SEARCH_RESULTS_SELECTED_SCHEMA_1_0_2 = 'iglu:com.fanduel/search_results_selected/jsonschema/1-0-2';

export const SNOWPLOW_EVENTS_MAPPING = {
    SCHEMAS: {
        game_clicked: BANNER_INTERACTION_SCHEMA_4_0_0,
        top_nav_impression: BANNER_INTERACTION_SCHEMA_4_0_2,
        top_nav_scroll: BANNER_INTERACTION_SCHEMA_4_0_2,
        top_nav_selected: BANNER_INTERACTION_SCHEMA_4_0_2,
        zone_initial_load: BANNER_INTERACTION_SCHEMA_4_0_0,
        zone_scrolled: BANNER_INTERACTION_SCHEMA_4_0_0,
        game_zone_impression: ZONE_INTERACTION_SCHEMA_1_0_1,
        game_zone_scroll: ZONE_INTERACTION_SCHEMA_1_0_1,
        game_zone_selected: ZONE_INTERACTION_SCHEMA_1_0_1,
        category_page_impression: ZONE_INTERACTION_SCHEMA_1_0_1,
        category_page_game_selected: ZONE_INTERACTION_SCHEMA_1_0_1,
        category_screen_view: 'iglu:com.snowplowanalytics.mobile/screen_view/jsonschema/1-0-0',
        game_favorited: 'iglu:com.fanduel/game_favorited/jsonschema/2-0-0',
        game_favorite_removed: 'iglu:com.fanduel/game_favorite_removed/jsonschema/1-0-0',
        info_icon_clicked: 'iglu:com.fanduel/info_icon_clicked/jsonschema/1-0-0',
        zone_interaction: ZONE_INTERACTION_SCHEMA_1_0_1,
        search_opened: SEARCH_OPENED_SCHEMA_1_0_1,
        search_initialized: SEARCH_INITIALIZED_SCHEMA_1_0_1,
        search_results_returned: SEARCH_RESULTS_RETURNED_SCHEMA_1_0_2,
        search_results_selected: SEARCH_RESULTS_SELECTED_SCHEMA_1_0_2,
        global_properties: 'iglu:com.fanduel/casino_global_properties/jsonschema/1-0-1',
        user_properties: 'iglu:com.fanduel/casino_user_properties/jsonschema/1-0-0',
        game_impression: GAME_ELEMENT_SCHEMA_4_0_1,
        navigation_menu_element: 'iglu:com.fanduel/navigation_menu_element/jsonschema/3-0-0'
    },
    CONTEXTS: {
        [SNOWPLOW_CONTEXT_MAPPING.GLOBAL_PROPERTIES]: {
            product: 'product',
            pageName: 'page_name',
            balance: 'balance',
            loginStatus: 'login_status',
            fullUrl: 'full_url',
            hostname: 'hostname',
            pagePath: 'page_path',
            pageTitle: 'page_title',
            siteVersion: 'site_version',
            sitePlatform: 'site_platform',
            jurisdiction: 'jurisdiction'
        },
        [SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION]: {
            zoneName: 'zone_name',
            zonePosition: 'zone_position',
            position: 'position',
            gameId: 'game_id',
            gameName: 'game_name',
            onScreen: 'on_screen',
            zoneType: 'zone_type',
            zoneLayout: 'zone_layout',
            internalCode: 'internal_code',
            triggerGameName: 'trigger_game_name',
            rankOrder: 'rank_order',
            spsRank: 'sps_rank',
            spsSpins: 'sps_spins',
            modelType: 'model_type',
            gameTileType: 'game_tile_type',
            automatedZone: 'automated_zone'
        },
        [SNOWPLOW_CONTEXT_MAPPING.NAVIGATION_MENU_ELEMENT]: {
            label: 'label',
            module: 'module',
            position: 'position',
            navigationMenuName: 'navigation_menu_name',
            onScreen: 'on_screen',
            pinned: 'pinned',
            personalized: 'personalized'
        },
        [SNOWPLOW_CONTEXT_MAPPING.ZONE_INTERACTION]: {
            action: 'action',
            automatedZone: 'automated_zone',
            ctaLabel: 'cta_label',
            pageName: 'page_name',
            zoneId: 'zone_id',
            zoneLayout: 'zone_layout',
            zoneName: 'zone_name',
            zonePosition: 'zone_position',
            zoneType: 'zone_type'
        }
    }
};

export type SnowplowSchemaKey = keyof typeof SNOWPLOW_EVENTS_MAPPING.SCHEMAS;

export const getSnowplowContext = (
    contextType: SnowplowSchemaKey,
    props: Record<string, unknown>
): ISnowplowContext => {
    return {
        schema: SNOWPLOW_EVENTS_MAPPING.SCHEMAS[contextType],
        data: mapPropsToKeys(props, contextType as EventType)
    };
};

export const initializeSnowplow = async (): Promise<ISnowplowUm> => {
    if (snowplowInstance) {
        return snowplowInstance;
    }

    if (initPromise) {
        return initPromise;
    }

    initPromise = (async () => {
        try {
            const { SnowplowUm } = await import('@fanduel/snowplow-um');
            const { initializeUnifiedModules } = await import('@/logic/unified-modules');

            await initializeUnifiedModules();
            snowplowInstance = SnowplowUm;

            return SnowplowUm;
        } catch (error) {
            console.error('[SnowplowModule.initialize] Error initializing Snowplow', error);
            initPromise = null;
            throw error;
        }
    })();

    return initPromise;
};

export const getSnowplowSessionId = async (): Promise<string | null> => {
    const snowplow = await initializeSnowplow();
    return snowplow.getClientSession().sessionId || null;
};

export const getNavigationMenuElementData = (
    label: string,
    position: number,
    module: string,
    navigationMenuName: string,
    onScreen: boolean,
    pinned: boolean,
    personalized: boolean
) => ({
    label,
    module,
    position,
    navigationMenuName,
    onScreen,
    pinned,
    personalized
});

/** Snowplow zone fields from components. */
export type GameZoneSnowplowStaticConfig = {
    zoneName: string;
    zoneType: string;
    zoneLayout?: string | null;
    automatedZone?: boolean | null;
};

/** Full game zone row including zone_position. */
export type GameZoneSnowplowConfig = GameZoneSnowplowStaticConfig & {
    zonePosition: number | null;
};

/**
 * Loose inputs for `game_element` 4-0-0 (camelCase keys match `SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION`).
 * `getGameElementData` fills `''` / `0` for required strings and integers; optional fields stay `null` when absent.
 */
export type GameElementDataParams = {
    zoneName?: string | null;
    zonePosition?: string | number | null;
    zoneType?: string | null;
    zoneLayout?: string | null;
    position?: string | number | null;
    gameId?: string | number | null;
    gameName?: string | null;
    onScreen?: boolean;
    internalCode?: string | null;
    triggerGameName?: string | null;
    rankOrder?: string | number | null;
    spsRank?: string | number | null;
    spsSpins?: string | number | null;
    modelType?: string | null;
    gameTileType?: 'static' | 'animated';
    automatedZone?: boolean | null;
};

/**
 * Builds `game_element` context props: one object with defaults
 * so Data Validity passes even when callers pass incomplete DOM / API values.
 */
export const getGameElementData = (p: GameElementDataParams): Record<string, unknown> => {
    const toStr = (v: string | number | null | undefined): string => String(v ?? '');
    const toInt = (v: string | number | null | undefined): number => {
        const n = Number(v);
        return Number.isFinite(n) ? Math.trunc(n) : 0;
    };
    const nullableStr = (v: string | null | undefined): string | null =>
        v === null || v === undefined || v === '' ? null : String(v);
    const nullableNum = (v: string | number | null | undefined, truncate = false): number | null => {
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        if (!Number.isFinite(n)) return null;
        return truncate ? Math.trunc(n) : n;
    };

    const modelTypeVal = String(p.modelType ?? '').toLowerCase();
    const modelType = modelTypeVal === 'challenger' || modelTypeVal === 'champion' ? modelTypeVal : null;

    return {
        zoneName: toStr(p.zoneName),
        zonePosition: toInt(p.zonePosition),
        zoneType: toStr(p.zoneType),
        position: toInt(p.position),
        gameId: toStr(p.gameId),
        gameName: toStr(p.gameName),
        onScreen: p.onScreen ?? false,
        zoneLayout: nullableStr(p.zoneLayout),
        internalCode: nullableStr(p.internalCode),
        triggerGameName: nullableStr(p.triggerGameName),
        rankOrder: nullableNum(p.rankOrder, true),
        spsRank: nullableNum(p.spsRank, true),
        spsSpins: nullableNum(p.spsSpins),
        gameTileType: toStr(p.gameTileType),
        ...(modelType !== null ? { modelType } : {}),
        ...(p.automatedZone !== undefined ? { automatedZone: p.automatedZone } : {})
    };
};

export const getGameElementDataFromZoneGame = (
    config: GameZoneSnowplowConfig,
    slotPosition: number,
    onScreen: boolean,
    game: GameTrackingInfo
): Record<string, unknown> =>
    getGameElementData({
        zoneName: config.zoneName,
        zonePosition: config.zonePosition,
        zoneType: config.zoneType,
        zoneLayout: config.zoneLayout ?? null,
        position: slotPosition,
        gameId: game.uid,
        gameName: getGameName(game.data.display_name),
        onScreen,
        internalCode: game.data.internal_code ?? null,
        triggerGameName: game.data.trigger_game_name ?? null,
        rankOrder: game.rank_order ?? null,
        spsRank: game.data.mean_sps_rank ?? null,
        spsSpins: game.data.mean_sps ?? null,
        modelType: game.data.model_type ?? null,
        automatedZone: config.automatedZone
    });

type SnowplowEventType = SnowplowSchemaKey;
type SnowplowEventProps = Record<string, unknown>;

export const getSnowplowEvent = (
    event: SnowplowEventType,
    eventProps: SnowplowEventProps,
    contexts: Array<{ contextType: string; contextProps: Record<string, unknown> }> = []
) => ({ destination: 'snowplow', event, eventProps, contexts });

export type SnowplowContext = {
    schema: string;
    data: Record<string, any>;
};
export type SnowplowEvent = {
    schema: string;
    data: Record<string, any>;
    context?: SnowplowContext[];
};

export type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

export const buildSnowplowEvent = (
    eventName: EventType,
    props: Record<string, unknown>,
    contexts: Array<{ contextType: string; contextProps: Record<string, unknown> }> = [],
    eventGlobalProperties: any
): SnowplowEvent => {
    const eventProps = mapPropsToKeys(props, eventName);

    const eventContexts = contexts.map(({ contextType, contextProps }) => {
        return getSnowplowContext(contextType as SnowplowSchemaKey, contextProps);
    });

    const globalContext = getSnowplowContext(
        SNOWPLOW_CONTEXT_MAPPING.GLOBAL_PROPERTIES as SnowplowSchemaKey,
        eventGlobalProperties
    );

    return {
        schema: SNOWPLOW_EVENTS_MAPPING.SCHEMAS[eventName],
        data: eventProps,
        context: [globalContext, ...eventContexts]
    };
};

type SnowplowGetterProps = {
    balance?: number;
    isLoggedIn?: boolean;
    region?: string;
    isSearchOpen?: boolean;
    isMobile?: boolean;
    isAndroidWrapper?: boolean;
    isIosWrapper?: boolean;
    pathname?: string;
};

const getProduct = () => {
    const product = process.env.PRODUCT;
    switch (product) {
        case 'casino':
            return 'casino_standalone_lobby';
        case 'poker':
            return 'poker_standalone_lobby';
        case 'xsell':
            return 'casino_xsell_lobby';
        default:
            console.warn('[Amplitude] Unknown product', product);
    }
};

const getAndroidDistribution = () => {
    const ua = (navigator?.userAgent || '').toLowerCase();
    switch (true) {
        case ua.includes('selfdist'):
            return 'side-load';
        case ua.includes('playstore'):
            return 'play-store';
        default:
            return '';
    }
};

const getSiteVersion = ({ isMobile, isWrapper }: { isMobile: boolean; isWrapper: boolean }) => {
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return 'native';
        default:
            return 'desktop';
    }
};

const getSitePlatform = ({
    isMobile,
    isAndroidWrapper,
    isIosWrapper
}: {
    isMobile: boolean;
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
}) => {
    const isWrapper = isAndroidWrapper || isIosWrapper;
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return isIosWrapper ? 'ios' : 'android';
        default:
            return 'desktop';
    }
};

export const getSnowplowProperties = ({
    balance,
    isLoggedIn,
    region,
    isSearchOpen,
    isMobile,
    isAndroidWrapper,
    isIosWrapper,
    pathname
}: SnowplowGetterProps): any => {
    const eventGlobalProperties: any = {
        fullUrl: window.location.href,
        hostname: window.location.hostname,
        pagePath: pathname ?? window.location.pathname,
        product: getProduct(),
        nationalJurisdiction: process.env.COUNTRY,
        androidDistributionMethod: getAndroidDistribution()
    };

    if (isMobile !== undefined && (isAndroidWrapper !== undefined || isIosWrapper !== undefined)) {
        eventGlobalProperties.siteVersion = getSiteVersion({
            isMobile,
            isWrapper: isAndroidWrapper || (isIosWrapper as boolean)
        });
    }

    if (isMobile !== undefined && isAndroidWrapper !== undefined && isIosWrapper !== undefined) {
        eventGlobalProperties.sitePlatform = getSitePlatform({ isMobile, isAndroidWrapper, isIosWrapper });
    }

    if (isSearchOpen !== undefined) {
        const pagePath = (pathname ?? window.location.pathname) || '/';
        eventGlobalProperties.pageName = isSearchOpen ? 'search' : getPageNameFromPathname(pagePath);
    }

    if (isLoggedIn !== undefined) {
        eventGlobalProperties.loginStatus = isLoggedIn ? 'logged_in' : 'logged_out';
    }

    if (region) {
        eventGlobalProperties.jurisdiction = region?.toLowerCase();
    }

    if (balance) {
        eventGlobalProperties.balance = Math.round(balance);
    }

    const androidDistribution = getAndroidDistribution();
    if (androidDistribution) {
        eventGlobalProperties.androidDistributionMethod = androidDistribution;
    }

    return eventGlobalProperties;
};
